<div class="w-full">
  <div>
    <ui-page-header title="Providers" subtitle="Manage API providers, adapters, and their configurations">
      <button uiButton size="sm" type="button" (click)="openAddModal()">
        <ng-icon name="lucidePlus" size="14" />
        <span>Add Provider</span>
      </button>
    </ui-page-header>

    <div class="mb-5 flex flex-wrap items-center gap-3">
      <div class="relative min-w-[220px] max-w-md flex-1">
        <ng-icon name="lucideSearch" size="16" class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" />
        <input
          uiInput
          type="text"
          class="!pr-8 !pl-9"
          placeholder="Search providers..."
          [(ngModel)]="searchQuery"
          (keyup.enter)="onFilterChange()"
        />
        @if (searchQuery) {
          <button type="button" class="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 leading-none text-muted hover:text-fg" (click)="onSearchClear()">
            <ng-icon name="lucideX" size="14" />
          </button>
        }
      </div>
      <select uiInput class="!w-auto" [(ngModel)]="typeFilter" (ngModelChange)="onFilterChange()">
        <option value="">All Types</option>
        <option value="FINANCIAL_ENTITY">Financial Entity</option>
        <option value="AGGREGATOR">Aggregator</option>
        <option value="GATEWAY">Gateway</option>
        <option value="PROXY">Proxy</option>
      </select>
      <div class="flex gap-1 rounded-xl border border-border bg-surface-muted p-1">
        <button
          type="button"
          class="cursor-pointer rounded-lg border-0 px-3 py-1.5 text-[0.6875rem] font-bold tracking-wide transition-colors"
          [class]="activeFilter === '' ? 'bg-brand text-white shadow-sm' : 'bg-transparent text-muted hover:text-fg'"
          (click)="activeFilter = ''; onFilterChange()"
        >
          All
        </button>
        <button
          type="button"
          class="cursor-pointer rounded-lg border-0 px-3 py-1.5 text-[0.6875rem] font-bold tracking-wide transition-colors"
          [class]="activeFilter === 'true' ? 'bg-brand text-white shadow-sm' : 'bg-transparent text-muted hover:text-fg'"
          (click)="activeFilter = 'true'; onFilterChange()"
        >
          Active
        </button>
        <button
          type="button"
          class="cursor-pointer rounded-lg border-0 px-3 py-1.5 text-[0.6875rem] font-bold tracking-wide transition-colors"
          [class]="activeFilter === 'false' ? 'bg-brand text-white shadow-sm' : 'bg-transparent text-muted hover:text-fg'"
          (click)="activeFilter = 'false'; onFilterChange()"
        >
          Inactive
        </button>
      </div>
    </div>

    @if (error(); as errMsg) {
      <ui-alert variant="danger">
        <div class="flex items-center gap-2.5">
          <ng-icon name="lucideX" size="18" />
          <p class="m-0 flex-1 font-semibold">{{ errMsg }}</p>
          <button type="button" class="cursor-pointer border-0 bg-transparent text-lg leading-none text-inherit" (click)="error.set(null)">×</button>
        </div>
      </ui-alert>
    }
    @if (success(); as msg) {
      <ui-alert variant="success">
        <p class="m-0 font-semibold">{{ msg }}</p>
      </ui-alert>
    }

    @if (loading()) {
      <div class="flex flex-col items-center gap-3 py-12 text-muted">
        <ui-spinner />
        <p class="m-0">Loading providers…</p>
      </div>
    }

    @if (!loading()) {
      @if (providers().length === 0) {
        <ui-empty-state title="No providers found.">
          <button uiButton variant="outline" size="sm" type="button" (click)="openAddModal()">
            <ng-icon name="lucidePlus" size="14" />
            <span>Add your first provider</span>
          </button>
        </ui-empty-state>
      } @else {
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 min-[1100px]:grid-cols-3 min-[1500px]:grid-cols-4">
          @for (provider of providers(); track provider.id) {
            <a
              class="block rounded-xl border border-border bg-card p-5 text-inherit no-underline shadow-sm transition-colors hover:border-primary"
              [class.opacity-60]="!provider.is_active"
              [routerLink]="shellRoutes.commands('manage', 'providers', provider.id, 'overview')"
            >
              <div class="flex gap-3.5">
                <div class="grid size-12 shrink-0 place-items-center rounded-xl" [class]="getDashIconClasses(provider.provider_type)">
                  <ng-icon name="lucideServer" size="24" />
                </div>
                <div class="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span class="truncate text-xs font-bold tracking-wider text-muted uppercase">{{ provider.name }}</span>
                  <span class="font-mono text-lg font-extrabold text-fg">{{ provider.code }}</span>
                  <div class="flex flex-wrap gap-1.5">
                    <ui-badge [variant]="getProviderTypeVariant(provider.provider_type)">
                      {{ getProviderTypeLabel(provider.provider_type) }}
                    </ui-badge>
                    @if (provider.adapter_class) {
                      <ui-badge variant="neutral" class="font-mono">{{ provider.adapter_class }}</ui-badge>
                    }
                  </div>
                  <div class="flex flex-wrap gap-2.5 text-xs text-muted">
                    @if (provider.country_code) {
                      <span>{{ provider.country_code }}</span>
                    }
                    @if (provider.currency_code) {
                      <span>{{ provider.currency_code }}</span>
                    }
                    <span>{{ provider.route_count }} routes</span>
                  </div>
                  <div class="mt-1 flex items-center justify-between gap-2">
                    <ui-badge [variant]="provider.is_active ? 'success' : 'neutral'">
                      {{ provider.is_active ? 'Active' : 'Inactive' }}
                    </ui-badge>
                    <div class="flex items-center gap-1.5" (click)="$event.preventDefault(); $event.stopPropagation()">
                      <button
                        type="button"
                        class="grid size-6 cursor-pointer place-items-center rounded-md border border-border bg-card text-muted hover:bg-surface-muted hover:text-fg"
                        title="Edit provider"
                        (click)="openEditModal(provider)"
                      >
                        <ng-icon name="lucidePencil" size="12" />
                      </button>
                      <button
                        type="button"
                        class="inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-full border px-2 text-[0.625rem] font-bold"
                        [class]="
                          provider.is_active
                            ? 'border-success-border bg-success-bg text-success-fg'
                            : 'border-border bg-surface-muted text-muted'
                        "
                        [title]="provider.is_active ? 'Deactivate' : 'Activate'"
                        (click)="toggleActive(provider, $event)"
                      >
                        <span class="size-1.5 rounded-full bg-current"></span>
                        <span>{{ provider.is_active ? 'ON' : 'OFF' }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </a>
          }
        </div>
      }
    }
  </div>

  <ui-modal [open]="modalOpen()" [title]="modalEditId ? 'Edit Provider' : 'Add Provider'" size="lg" (closed)="closeModal()">
    <app-provider-form
      [(value)]="modalForm"
      [entityLabel]="entityLabel"
      [countryLabel]="countryLabel"
      [currencyLabel]="currencyLabel"
      idPrefix="providers-modal"
    />

    <div class="mt-2">
      <div class="mb-2 flex items-center justify-between">
        <span class="text-sm font-semibold text-fg">Configuration</span>
        <div class="inline-flex gap-1 rounded-lg border border-border bg-surface-muted p-0.5">
          <button
            type="button"
            class="inline-flex cursor-pointer items-center gap-1 rounded-md border-0 px-2.5 py-1 text-xs font-semibold"
            [class]="modalConfigMode === 'kv' ? 'bg-card text-fg shadow-sm' : 'bg-transparent text-muted'"
            (click)="switchModalConfigMode('kv')"
          >
            <ng-icon name="lucideList" size="13" />
            <span>K/V</span>
          </button>
          <button
            type="button"
            class="inline-flex cursor-pointer items-center gap-1 rounded-md border-0 px-2.5 py-1 text-xs font-semibold"
            [class]="modalConfigMode === 'json' ? 'bg-card text-fg shadow-sm' : 'bg-transparent text-muted'"
            (click)="switchModalConfigMode('json')"
          >
            <ng-icon name="lucideCodeXml" size="13" />
            <span>JSON</span>
          </button>
        </div>
      </div>

      @if (modalConfigMode === 'kv') {
        <div class="flex flex-col gap-2">
          @for (row of modalConfigKvRows; track $index) {
            <div class="flex items-center gap-2">
              <input uiInput size="sm" type="text" class="flex-1 font-mono" placeholder="key" [(ngModel)]="row.key" />
              <input uiInput size="sm" type="text" class="flex-[2] font-mono" placeholder="value" [(ngModel)]="row.value" />
              <button type="button" class="grid size-6 shrink-0 cursor-pointer place-items-center rounded-md border-0 bg-transparent text-muted hover:text-danger" (click)="removeModalConfigRow($index)" title="Remove row">
                <ng-icon name="lucideX" size="12" />
              </button>
            </div>
          }
          <div>
            <button uiButton variant="outline" size="sm" type="button" (click)="addModalConfigRow()">
              <ng-icon name="lucidePlus" size="12" />
              <span>Add Row</span>
            </button>
          </div>
        </div>
      } @else {
        <textarea
          uiInput
          class="font-mono"
          rows="6"
          [(ngModel)]="modalConfigJson"
          placeholder='{ "base_url": "https://...", "timeout": 30 }'
        ></textarea>
      }
    </div>

    <ng-container modal-footer>
      <button uiButton variant="outline" size="sm" type="button" (click)="closeModal()">Cancel</button>
      <button uiButton size="sm" type="button" [disabled]="modalSaving()" (click)="saveModal()">
        @if (modalSaving()) {
          <ui-spinner size="sm" />
        }
        <span>{{ modalEditId ? 'Save Changes' : 'Create Provider' }}</span>
      </button>
    </ng-container>
  </ui-modal>
</div>
